import { useMemo, useState } from 'react'
import { Download, GitBranch, Pencil, Plus, Send, Trash2 } from 'lucide-react'
import {
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  IconButton,
  StatusBadge,
  textColumn,
  useToast,
  type ColumnDef,
} from '@/components/ui'
import {
  templateDownloadUrl,
  useContractTemplates,
  useDeleteContractTemplate,
} from '../api/templatesApi.ts'
import type { ContractTemplate } from '../types.ts'
import { TemplateUploadModal } from '../components/TemplateUploadModal.tsx'
import { TemplateVersionHistoryModal } from '../components/TemplateVersionHistoryModal.tsx'
import { BulkSendWizard } from '../components/BulkSendWizard.tsx'

export function TemplatesSection() {
  const { toast } = useToast()
  const { data: rows = [], isLoading, error, refetch } = useContractTemplates()
  const del = useDeleteContractTemplate()
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<ContractTemplate | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<ContractTemplate | null>(null)
  const [historyTarget, setHistoryTarget] = useState<ContractTemplate | null>(null)
  const [bulkSendTarget, setBulkSendTarget] = useState<ContractTemplate | null>(null)

  const columns = useMemo<ColumnDef<ContractTemplate, unknown>[]>(
    () => [
      textColumn<ContractTemplate>('name', 'Name'),
      textColumn<ContractTemplate>('productName', 'Produkt'),
      {
        accessorKey: 'placeholders',
        header: 'Platzhalter',
        cell: ({ getValue }) => {
          const arr = (getValue() as string[] | null) ?? []
          if (arr.length === 0) return <span className="text-caption text-gray-400">—</span>
          return (
            <div className="flex flex-wrap gap-1">
              {arr.slice(0, 6).map((p) => (
                <Badge key={p} variant="neutral" size="sm">{p}</Badge>
              ))}
              {arr.length > 6 ? <span className="text-caption text-gray-500">+{arr.length - 6}</span> : null}
            </div>
          )
        },
      },
      {
        accessorKey: 'version',
        header: 'Version',
        size: 90,
        cell: ({ getValue, row }) => (
          <div className="flex items-center gap-1">
            <span className="text-caption text-gray-500">v{getValue() as number}</span>
            {row.original.isMaterialChange && (
              <Badge variant="warning" size="sm">Wesentl.</Badge>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'fileSize',
        header: 'Größe',
        size: 100,
        cell: ({ getValue }) => {
          const n = getValue() as number | null
          return n ? <span className="tabular-nums text-caption">{(n / 1024).toFixed(0)} KB</span> : <span className="text-caption text-gray-400">—</span>
        },
      },
      {
        accessorKey: 'active',
        header: 'Aktiv',
        size: 90,
        cell: ({ getValue }) => <StatusBadge status={getValue() ? 'active' : 'inactive'} />,
      },
      {
        id: '__actions__',
        header: '',
        size: 170,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <IconButton
              icon={<Download className="w-4 h-4" />}
              aria-label="Original herunterladen"
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                window.open(templateDownloadUrl(row.original.id), '_blank', 'noopener')
              }}
            />
            <IconButton
              icon={<GitBranch className="w-4 h-4" />}
              aria-label="Versionsverlauf"
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); setHistoryTarget(row.original) }}
            />
            <IconButton
              icon={<Send className="w-4 h-4" />}
              aria-label="Update-Versand"
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); setBulkSendTarget(row.original) }}
            />
            <IconButton
              icon={<Pencil className="w-4 h-4" />}
              aria-label="Bearbeiten"
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); setEditing(row.original) }}
            />
            <IconButton
              icon={<Trash2 className="w-4 h-4" />}
              aria-label="Löschen"
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); setConfirmDelete(row.original) }}
            />
          </div>
        ),
      },
    ],
    [],
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-small text-gray-600">
          Vertragsvorlagen als DOCX mit Platzhaltern. Aus jedem Vertrag heraus kann eine Vorlage gerendert werden — die Brücke zur Unterschrift folgt mit Etappe 7b.
        </p>
        <Button variant="primary" size="sm" leadingIcon={<Plus className="w-4 h-4" />} onClick={() => setCreateOpen(true)}>
          Neue Vorlage
        </Button>
      </div>

      <DataTable<ContractTemplate>
        data={rows}
        columns={columns}
        getRowId={(t) => t.id}
        loading={isLoading}
        error={error ? 'Vorlagen konnten nicht geladen werden.' : null}
        onRetry={() => void refetch()}
        searchPlaceholder="Vorlagen suchen …"
        onRowClick={(t) => setEditing(t)}
        emptyState={
          <EmptyState
            title="Noch keine Vertragsvorlage"
            description={`Lade eine DOCX-Datei mit Platzhaltern (z. B. {vertragsnummer}, {kunde_name}) hoch — dann steht sie in jedem Vertrag als „Aus Vorlage generieren" zur Verfügung.`}
            action={
              <Button variant="primary" size="sm" leadingIcon={<Plus className="w-4 h-4" />} onClick={() => setCreateOpen(true)}>
                Neue Vorlage
              </Button>
            }
          />
        }
      />

      <TemplateUploadModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSaved={() => setCreateOpen(false)}
      />
      <TemplateUploadModal
        open={Boolean(editing)}
        onOpenChange={(o) => !o && setEditing(null)}
        template={editing}
        onSaved={() => setEditing(null)}
      />
      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Vorlage löschen?"
        message={
          confirmDelete
            ? `„${confirmDelete.name}" wird gelöscht. Wenn Verträge generierte Dokumente aus dieser Vorlage haben, wird das Löschen abgelehnt.`
            : ''
        }
        variant="danger"
        confirmLabel="Löschen"
        loading={del.isPending}
        onConfirm={() => {
          if (!confirmDelete) return
          del.mutate(confirmDelete.id, {
            onSuccess: () => {
              toast({ title: 'Vorlage gelöscht', tone: 'success' })
              setConfirmDelete(null)
            },
            onError: (err) =>
              toast({
                title: 'Löschen nicht möglich',
                description: err instanceof Error ? err.message : 'Unbekannter Fehler',
                tone: 'danger',
              }),
          })
        }}
        onCancel={() => setConfirmDelete(null)}
      />

      <TemplateVersionHistoryModal
        template={historyTarget}
        open={Boolean(historyTarget)}
        onOpenChange={(o) => !o && setHistoryTarget(null)}
      />

      <BulkSendWizard
        template={bulkSendTarget}
        open={Boolean(bulkSendTarget)}
        onOpenChange={(o) => !o && setBulkSendTarget(null)}
      />
    </div>
  )
}
